import {
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import { useHasAdminFeatureAccess } from "@/hooks/useHasAdminFeatureAccess";
import { Building2, ChevronLeft, Settings, Tags, User, Users, type LucideIcon } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

/**
 * Settings row of the NavUserDropdown. Org admins (with the self-serve flag on)
 * get a submenu — Manage Team / My Profile. Everyone else gets a single
 * Settings link straight to their profile.
 */


/** Single nav row: dropdown item that renders an `<a>` via react-router `<Link>`. */
function NavMenuLink({ to, icon: Icon, label }: { to: string; icon: LucideIcon; label: string }) {
  return (
    <DropdownMenuItem asChild className="cursor-pointer p-0 hover:!bg-gray-100 hover:!text-black">
      <Link to={to} className="flex w-full items-center gap-2 p-2 !text-black">
        <Icon className="h-4 w-4 shrink-0" />
        <span>{label}</span>
      </Link>
    </DropdownMenuItem>
  );
}

export function DropDownMenuSettingsOption() {
  const navigate = useNavigate();
  const showAdminSettings = useHasAdminFeatureAccess("SELF_SERVE_ADMIN_ENABLED");
  const showManageOffices = useHasAdminFeatureAccess("MANAGE_OFFICES_ENABLED");
  const showPhotoLabels = useHasAdminFeatureAccess("PHOTO_LABELS_ENABLED");

  if (!showAdminSettings) {
    return <NavMenuLink to="/settings/profile" icon={Settings} label="Settings" />;
  }

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger
        suppressChevron
        onClick={() => navigate("/settings/users")}
        className="flex w-full items-center gap-2 !bg-white p-2 hover:!bg-gray-100 hover:!text-black"
      >
        <ChevronLeft className="h-4 w-4 shrink-0" />
        <Settings className="h-4 w-4 shrink-0" />
        <span className="min-w-0 flex-1 text-left">Settings</span>
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="!min-w-[12rem] !border-gray-200 !bg-white !text-black">
        <NavMenuLink to="/settings/users" icon={Users} label="Team" />
        {showManageOffices && (
          <NavMenuLink to="/settings/offices" icon={Building2} label="Offices" />
        )}
        {showPhotoLabels && <NavMenuLink to="/settings/photo-labels" icon={Tags} label="Photo Labels" />}
        <NavMenuLink to="/settings/profile" icon={User} label="Profile" />
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}
